import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faCoins, faArrowUp, faArrowDown, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { UiPageHeader, UiStatsGrid, UiFilterPanel } from '../components/ui';
import type { StatCardDef } from '../components/ui';
import DataTable from '../components/shared/DataTable';
import type { Column } from '../components/shared/DataTable';
import { useServerPagination } from '../hooks/useServerPagination';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

interface LedgerRow {
  type: string;
  direction: string;
  amount: number;
  balance_after: number;
  ref_type?: string;
  note?: string;
  created_at: string;
}

interface FeeWallet {
  balance: number;
  total_credit: number;
  total_debit: number;
  ledger: LedgerRow[];
  ledger_total?: number;
}

const fmt = (n: any) => `฿${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const LEDGER_TYPE: Record<string, string> = {
  topup: 'เติมเครดิต', fee_payin: 'ค่าธรรมเนียมฝาก', fee_payout: 'ค่าธรรมเนียมถอน',
  adjustment: 'ปรับยอด', reversal: 'คืนยอด',
};

const CARD = 'bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700';

const P2pOverviewPage: React.FC = () => {
  const [fee, setFee] = useState<FeeWallet | null>(null);
  const [loading, setLoading] = useState(true);
  // ledger ใช้ server pagination — รายการเยอะแค่ไหนก็เปิดย้อนหลังได้ครบ (เดิม backend cap 50 แถว)
  const { currentPage, pageSize, setCurrentPage, setTotalItems, serverPaginationProps } = useServerPagination(10);

  // ── ค้นหา (debounce) + กรองวันที่ (default = วันนี้) ──
  const today = dayjs().tz(TZ).format('YYYY-MM-DD');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [appliedDateFrom, setAppliedDateFrom] = useState(today);
  const [appliedDateTo, setAppliedDateTo] = useState(today);

  useEffect(() => {
    const delay = setTimeout(() => { setSearchTerm(searchInput); setCurrentPage(1); }, 400);
    return () => clearTimeout(delay);
  }, [searchInput, setCurrentPage]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', String(pageSize));
      params.append('offset', String((currentPage - 1) * pageSize));
      if (searchTerm) params.append('search', searchTerm);
      if (appliedDateFrom) params.append('start_date', appliedDateFrom);
      if (appliedDateTo) params.append('end_date', appliedDateTo);
      const res = await merchantApi.get(`/merchants/p2p-fee-wallet?${params.toString()}`);
      if (res.data.success) { setFee(res.data.data); setTotalItems(res.data.data.ledger_total || 0); }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, appliedDateFrom, appliedDateTo, setTotalItems]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) { toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด'); return; }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setDateFrom(today); setDateTo(today);
    setAppliedDateFrom(today); setAppliedDateTo(today);
    setSearchInput(''); setSearchTerm('');
    setCurrentPage(1);
  };

  const ledgerColumns: Column<LedgerRow>[] = [
    {
      key: 'type', header: 'รายการ',
      render: (l) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-800 dark:text-slate-100">{LEDGER_TYPE[l.type] || l.type}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500 truncate">
            {l.note ? `${l.note} · ` : ''}{new Date(l.created_at).toLocaleString('th-TH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      ),
    },
    {
      key: 'amount', header: 'จำนวน', width: '170px',
      render: (l) => (
        <p className={`text-sm font-bold tabular-nums ${l.direction === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
          <FontAwesomeIcon icon={l.direction === 'credit' ? faArrowDown : faArrowUp} className="mr-1 text-[10px]" />
          {l.direction === 'credit' ? '+' : '−'}{fmt(l.amount)}
        </p>
      ),
    },
    {
      key: 'balance_after', header: 'คงเหลือ', width: '150px',
      render: (l) => <span className="text-sm tabular-nums text-gray-600 dark:text-slate-300">{fmt(l.balance_after)}</span>,
    },
  ];

  const statCards: StatCardDef[] = [
    {
      icon: faCoins, iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'เครดิตค่าธรรมเนียม P2P คงเหลือ',
      value: <span className="text-gray-800 dark:text-slate-100">{fmt(fee?.balance || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">เติมล่วงหน้าโดยผู้ดูแลระบบ</span>,
    },
    {
      icon: faArrowDown, iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'เติมสะสม',
      value: <span className="text-gray-800 dark:text-slate-100">{fmt(fee?.total_credit || 0)}</span>,
    },
    {
      icon: faArrowUp, iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'หักสะสม (ค่าธรรมเนียม)',
      value: <span className="text-gray-800 dark:text-slate-100">{fmt(fee?.total_debit || 0)}</span>,
    },
  ];

  return (
    <>
      <UiPageHeader
        icon={faChartPie}
        title="P2P Overview"
        subtitle="ภาพรวมกระเป๋าค่าธรรมเนียม P2P และประวัติการเคลื่อนไหว"
      />

      <UiStatsGrid cards={statCards} loading={loading && !fee} cols={3} />

      <div className={`${CARD} p-5 mt-4`}>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-3">ประวัติกระเป๋าค่าธรรมเนียม (รวมการเติม)</h2>

        <UiFilterPanel
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onSearch={handleSearch}
          onReset={handleReset}
          loading={loading}
        >
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1"><FontAwesomeIcon icon={faSearch} className="mr-1" />ค้นหา</label>
            <div className="relative">
              <input
                type="text" placeholder="ค้นหาหมายเหตุ/อ้างอิง..." value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 pr-7"
              />
              {searchInput && <button type="button" onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"><FontAwesomeIcon icon={faTimes} className="text-xs" /></button>}
            </div>
          </div>
        </UiFilterPanel>

        <DataTable
          data={fee?.ledger || []}
          columns={ledgerColumns}
          loading={loading && !fee}
          emptyMessage="ยังไม่มีรายการ"
          serverPagination={serverPaginationProps}
        />
      </div>
    </>
  );
};

export default P2pOverviewPage;
